<template>
  <div>
    <label for="metrics" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
      >Send to</label
    >
    <div class="mt-2 grid grid-cols-1">
      <select
        id="metrics"
        name="metrics"
        v-model="selected"
        :disabled="loading || (!canChange && !modelValue)"
        class="w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:*:bg-gray-800 dark:disabled:bg-white/10"
        :aria-describedby="describedBy"
      >
        <option value="">None</option>
        <option
          v-for="connection in connections"
          :key="connection.id"
          :value="connection.id"
          :disabled="!canChange && connection.id !== modelValue"
        >
          {{ connection.name }}
        </option>
      </select>
    </div>
    <p v-if="error" id="metrics-error" class="mt-2 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p>
    <p v-else id="metrics-desc" class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      An OpenTelemetry endpoint that receives the metrics every minute. Manage connections on the
      <router-link
        :to="{ name: 'Metrics' }"
        class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
        >Metrics</router-link
      >
      page.
    </p>
    <div
      v-if="warning"
      id="metrics-warning"
      class="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
      role="alert"
    >
      <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
      <p>{{ warning }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid';
import { asProblem } from '@/api/caches';
import { listMetricsConnections, type MetricsConnection } from '@/api/metrics-connections';
import { useMonitoringEntitlement } from '@/lib/monitoring-entitlement';

const props = defineProps<{
  /** The selected connection id, or an empty string for none. */
  modelValue: string;
  warning?: string;
}>();

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>();

// v-model on the select re-applies the selection once the options have loaded.
const selected = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
});

const { canChange } = useMonitoringEntitlement();
const connections = ref<MetricsConnection[]>([]);
const loading = ref(true);
const error = ref('');

const describedBy = computed(() =>
  [error.value ? 'metrics-error' : 'metrics-desc', props.warning ? 'metrics-warning' : '']
    .filter(Boolean)
    .join(' ')
);

onMounted(async () => {
  try {
    connections.value = await listMetricsConnections();
  } catch (failure) {
    const e = asProblem(failure);
    error.value = e?.response?.data?.detail || e?.message || 'Failed to load metrics connections.';
  } finally {
    loading.value = false;
  }
});
</script>
